import React, { useState } from "react";
import {
  Button,
  NumberInput,
  required,
  SelectInput,
  ArrayInput,
  useNotify,
  useRecordContext,
  useRefresh,
  SimpleFormIterator,
  FormDataConsumer,
  minValue,
  maxValue,
} from "react-admin";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { administrationRequests } from "api/administrationApi";
import { SemestersChoices } from "utils/choices";
import { settings } from "app";
import FormModal from "server/components/Modals/FormModal";
import dataProvider from "server/dataProvider";
import { useNotifyError } from "utils/useNotifyError";

const EMPTY_ROW = { semester: null, weeks: null };

const WeeksSpecificationForm = ({ label = "Седмици по семестри" }) => {
  const record = useRecordContext();
  const notify = useNotify();
  const notifyError = useNotifyError();
  const refresh = useRefresh();

  const [studyWeeks, setStudyWeeks] = useState([EMPTY_ROW]);

  if (!record?.id) return null;

  // onOpen се await-ва преди отварянето, затова studyWeeks вече е зареден,
  // когато Dialog монтира <Form> с тези defaultValues.
  const defaultValues = { studyWeeks };

  const loadStudyWeeks = async () => {
    try {
      const { data } = await dataProvider.customRequest(
        administrationRequests.getSpecialityStudyWeeks(record.id),
        null,
        "GET",
      );
      setStudyWeeks(Array.isArray(data) && data.length > 0 ? data : [EMPTY_ROW]);
    } catch (error) {
      // 404 = специалността още няма спецификация → отваряме с празен ред
      if (error?.status === 404) {
        setStudyWeeks([EMPTY_ROW]);
        return;
      }
      notifyError(error);
      return false;
    }
  };

  const handleConfirm = async ({ studyWeeks: rows }) => {
    await dataProvider.customRequest(
      administrationRequests.updateSpecialityStudyWeeks(record.id),
      { studyWeeks: rows },
      "PUT",
    );
    notify("Успешно заявена промяна", { type: "success" });
    refresh();
  };

  return (
    <FormModal
      titleContent={`Седмици по семестри за специалност ${record.nameBg}`}
      saveTitle="Запази"
      defaultValues={defaultValues}
      onOpen={loadStudyWeeks}
      onConfirm={handleConfirm}
      triggerButton={
        <Button label={label}>
          <ListAltIcon />
        </Button>
      }
    >
      <ArrayInput
        source="studyWeeks"
        label=""
      >
        <SimpleFormIterator
          disableClear
          sx={settings.style.iteratorWithoutGap}
        >
          <FormDataConsumer>
            {({ formData, scopedFormData }) => {
              const selectedSemesters = (formData?.studyWeeks ?? []).map(item => item?.semester);
              const currentSemester = scopedFormData?.semester;
              const available = SemestersChoices.filter(
                option => option.id === currentSemester || !selectedSemesters.includes(option.id),
              );
              return (
                <SelectInput
                  source="semester"
                  label="Семестър"
                  choices={available}
                  optionValue="id"
                  optionText="name"
                  validate={[required()]}
                />
              );
            }}
          </FormDataConsumer>
          <NumberInput
            source="weeks"
            label="Брой седмици"
            validate={[
              required(),
              minValue(1, "Броят седмици трябва да е между 1 и 15"),
              maxValue(15, "Броят на седмиците не може да е по-голям от 15"),
            ]}
          />
        </SimpleFormIterator>
      </ArrayInput>
    </FormModal>
  );
};

export default WeeksSpecificationForm;
